/**
 * Catálogo de textos em português brasileiro para o recurso `auth` (entrada,
 * registro, verificação de link mágico, redefinição/definição de senha). As
 * chaves mapeiam 1:1 para as chamadas `t('auth.*')` em
 * `packages/@gamecodex/world-ui/src/features/auth/*`. Os valores são as
 * cadeias de texto exatas traduzidas para português brasileiro.
 */
export const authPtBR = {
  // ── AuthShell ────────────────────────────────────────────────────────────
  'auth.shell.homeAriaLabel': 'Página inicial do Grymwyrd',
  'auth.shell.logoAlt': 'Grymwyrd',

  // ── OAuthButtons ─────────────────────────────────────────────────────────
  'auth.oauth.continueWithGoogle': 'Continuar com Google',
  'auth.oauth.continueWithDiscord': 'Continuar com Discord',

  // ── AccountVerifyPage ────────────────────────────────────────────────────
  'auth.verify.seoTitle': 'Entrando',
  'auth.verify.seoDescription': 'Verificando seu link de entrada no Grymwyrd.',
  'auth.verify.errorMissingToken':
    'Este link de entrada está sem seu token. Solicite um novo a partir da página de entrada.',
  'auth.verify.errorInvalidOrExpired':
    'Este link de entrada é inválido ou expirou. Solicite um novo a partir da página de entrada.',
  'auth.verify.confirming': 'Confirmando sua entrada…',
  'auth.verify.signedInHeading': 'Você entrou',
  'auth.verify.signedInAsPrefix': 'Conectado como',
  'auth.verify.signedInAsSuffix': '. Levando você para seu santuário…',
  'auth.verify.takingYouToSanctum': 'Levando você para seu santuário…',
  'auth.verify.goNow': 'Ir agora',
  'auth.verify.problemHeading': 'Problema com o link de entrada',
  'auth.verify.backToSignIn': 'Voltar para entrada',

  // ── LoginPage ────────────────────────────────────────────────────────────
  'auth.login.errorEnterEmailFirst': 'Digite seu email acima e solicite um link.',
  'auth.login.seoTitle': 'Atravesse o Limiar',
  'auth.login.seoDescription': 'Entre no Grymwyrd e retorne à sua lenda no crepúsculo eterno.',
  'auth.login.eyebrow': 'Retorne, Peregrino',
  'auth.login.title': 'Atravesse o Limiar',
  'auth.login.blurb': 'Pronuncie seu nome e a palavra secreta para retornar ao crepúsculo eterno.',
  'auth.login.noPactYet': 'Nenhum pacto ainda?',
  'auth.login.forgeYourLegend': 'Forje sua lenda',
  'auth.login.emailLabel': 'Email',
  'auth.login.emailPlaceholder': 'voce@grymwyrd.com',
  'auth.login.passwordLabel': 'Senha',
  'auth.login.forgotPassword': 'Esqueceu sua senha?',
  'auth.login.submitBusy': 'Atravessando…',
  'auth.login.submit': 'Entrar',
  'auth.login.dividerOr': 'ou',
  'auth.login.magicSentPrefix': 'Um link de entrada está a caminho',
  'auth.login.magicSentTo': 'para',
  'auth.login.magicSentSuffix': '. Verifique sua caixa de entrada.',
  'auth.login.emailMeSignInLink': 'Me envie um link de entrada por email',
  'auth.login.mockAuthPrefix': 'Autenticação mock de desenvolvimento: qualquer email funciona. Use',
  'auth.login.mockAuthOr': 'ou',
  'auth.login.mockAuthSuffix': 'para papéis elevados.',

  // ── RegisterPage ─────────────────────────────────────────────────────────
  'auth.register.errorDisplayNameRequired': 'Escolha o nome sob o qual sua lenda será contada.',
  'auth.register.errorEmailRequired': 'Um email é necessário para selar seu pacto.',
  'auth.register.errorEmailInvalid': 'Este email não parece correto.',
  'auth.register.errorPasswordTooShort': 'Sua palavra secreta deve ter pelo menos 8 caracteres.',
  'auth.register.errorMustBeAdult': 'Você deve ter 13 anos ou mais para entrar no Grymwyrd.',
  'auth.register.errorMustAcceptTerms': 'Você deve aceitar o pacto (termos) para continuar.',
  'auth.register.errorIncomplete': 'Algumas marcas em seu pacto estão incompletas. Veja abaixo.',
  'auth.register.seoTitle': 'Comece Sua Jornada',
  'auth.register.seoDescription':
    'Crie uma conta no Grymwyrd, levante um avatar e deixe sua história se tornar canônica.',
  'auth.register.eyebrow': 'Explore o Vilarejo de Greywick',
  'auth.register.title': 'Comece Sua Jornada',
  'auth.register.blurb': 'Você acorda dos sonhos etéreos e abre seus olhos…',
  'auth.register.alreadyBound': 'Já vinculado ao crepúsculo?',
  'auth.register.loginToExisting': 'Conecte-se a Sua Conta Existente',
  'auth.register.displayNameLabel': 'Nome de exibição',
  'auth.register.displayNamePlaceholder': 'O Cavaleiro Oco',
  'auth.register.displayNameHint': 'Isto nomeia seu avatar público e contribuições.',
  'auth.register.emailLabel': 'Email',
  'auth.register.emailPlaceholder': 'voce@grymwyrd.com',
  'auth.register.passwordLabel': 'Senha',
  'auth.register.passwordPlaceholder': 'Pelo menos 8 caracteres',
  'auth.register.agreementsLegend': 'Acordos',
  'auth.register.adultConfirmPrefix': 'Confirmo que tenho',
  'auth.register.adultConfirmEmphasis': '13 anos de idade ou mais',
  'auth.register.adultConfirmSuffix': '.',
  'auth.register.acceptPrefix': 'Aceito os',
  'auth.register.termsOfThePact': 'Termos do Pacto',
  'auth.register.acceptSuffix': '.',
  'auth.register.submitBusy': 'Selando o pacto…',
  'auth.register.submit': 'Emergir',
  'auth.register.dividerOr': 'ou',
  'auth.register.mockAuthNote': 'Autenticação mock de desenvolvimento: registro persiste apenas localmente. Nenhum email é enviado.',
  'auth.register.lastUpdated': 'Última atualização em {date}',

  // ── ResetPasswordPage ────────────────────────────────────────────────────
  'auth.reset.errorInvalidEmail': 'Digite o email vinculado ao seu pacto.',
  'auth.reset.seoTitle': 'Redefinir Sua Senha',
  'auth.reset.seoDescription':
    'Esqueceu sua senha? Enviaremos um link único por email; clique nele para escolher uma nova senha.',
  'auth.reset.eyebrow': 'Perdeu a Palavra',
  'auth.reset.title': 'Redefinir Sua Senha',
  'auth.reset.blurb':
    'Sussurre seu email e um link único será enviado para a escuridão. Siga-o de volta e você definirá uma nova senha.',
  'auth.reset.rememberedAfterAll': 'Lembrou depois de tudo?',
  'auth.reset.loginToExisting': 'Conecte-se a Sua Conta Existente',
  'auth.reset.successMessage':
    'Se uma conta existe para {email}, um link único foi enviado. Abra-o para entrar e definir sua nova senha — verifique seu email e a câmara de spam.',
  'auth.reset.sendToDifferentEmail': 'Enviar para um email diferente',
  'auth.reset.emailLabel': 'Email',
  'auth.reset.emailPlaceholder': 'voce@grymwyrd.com',
  'auth.reset.submitBusy': 'Convocando…',
  'auth.reset.submit': 'Enviar Link de Entrada',

  // ── SetPasswordPage ──────────────────────────────────────────────────────
  'auth.setPassword.errorTooShort': 'Escolha uma senha com pelo menos 8 caracteres.',
  'auth.setPassword.errorMismatch': 'Essas senhas não correspondem.',
  'auth.setPassword.seoTitle': 'Defina Sua Senha',
  'auth.setPassword.seoDescription': 'Escolha uma nova senha para sua conta no Grymwyrd.',
  'auth.setPassword.eyebrow': 'Sele o Pacto',
  'auth.setPassword.title': 'Defina uma Nova Senha',
  'auth.setPassword.blurb':
    'Você entrou. Escolha uma nova senha — você poderá usá-la na próxima vez junto com o link mágico.',
  'auth.setPassword.ratherNot': 'Prefere não?',
  'auth.setPassword.skipForNow': 'Pular por enquanto',
  'auth.setPassword.successMessage': 'Sua senha está definida. Levando você para seu santuário…',
  'auth.setPassword.newPasswordLabel': 'Nova senha',
  'auth.setPassword.newPasswordPlaceholder': 'Pelo menos 8 caracteres',
  'auth.setPassword.confirmPasswordLabel': 'Confirme a senha',
  'auth.setPassword.confirmPasswordPlaceholder': 'Digite novamente',
  'auth.setPassword.submitBusy': 'Selando…',
  'auth.setPassword.submit': 'Salvar Senha',
}
